import { useWizardContext } from "./shared";
import { Button } from "@/components/ui/button";

export function WelcomeScreen() {
  const { state, dispatch } = useWizardContext();
  const p = state.payload!;
  const primary = p.session.occupants.find((o) => o.role === "primary");
  const firstName = primary?.firstName?.trim() || "there";

  const arrival = formatDate(p.chain.arrivalDate);
  const departure = formatDate(p.chain.departureDate);
  const nights = nightsBetween(p.chain.arrivalDate, p.chain.departureDate);

  // Aggregate room-type categories + occupancy across all rooms in the chain.
  const totals = p.chain.rooms.reduce(
    (acc, r) => {
      acc.adults += r.adults;
      acc.children += r.children;
      acc.infants += r.infants;
      return acc;
    },
    { adults: 0, children: 0, infants: 0 }
  );

  // Group by room-type name: "1 × Standard Double, 2 × Superior Sea View".
  const typeCounts = new Map<string, number>();
  for (const r of p.chain.rooms) {
    const label = r.roomTypeName?.trim() || "Room";
    typeCounts.set(label, (typeCounts.get(label) ?? 0) + 1);
  }
  const roomTypeSummary = [...typeCounts.entries()]
    .map(([label, n]) => (n > 1 ? `${n} × ${label}` : label))
    .join(", ");

  return (
    <div>
      <h1 className="screen-h1">Welcome, {firstName}.</h1>
      <p className="screen-p mt-2">
        We've put together a quick pre-check-in for your stay at <strong>{p.property.name}</strong>.
        It takes about 3 minutes — we'll skip the paperwork at the desk.
      </p>

      <div className="mt-5 space-y-3 rounded-xl border bg-muted/40 p-4">
        <Row label="Arrival"   value={arrival} />
        <Row label="Departure" value={departure} />
        <Row label="Nights"    value={String(nights)} />
        <Row
          label={p.chain.rooms.length === 1 ? "Room" : "Rooms"}
          value={roomTypeSummary || `${p.chain.rooms.length}`}
        />
        <Row label="Guests" value={formatGuests(totals)} />
      </div>

      <Button className="mt-8 w-full h-11" onClick={() => dispatch({ type: "next" })}>
        Get started
      </Button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
}

function formatGuests({ adults, children, infants }: { adults: number; children: number; infants: number }): string {
  const parts: string[] = [];
  if (adults  > 0) parts.push(`${adults} adult${adults  !== 1 ? "s" : ""}`);
  if (children > 0) parts.push(`${children} child${children !== 1 ? "ren" : ""}`);
  if (infants  > 0) parts.push(`${infants} infant${infants !== 1 ? "s" : ""}`);
  return parts.join(", ") || "—";
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function nightsBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  if (!ay || !am || !ad || !by || !bm || !bd) return 0;
  const start = Date.UTC(ay, am - 1, ad);
  const end = Date.UTC(by, bm - 1, bd);
  return Math.round((end - start) / 86_400_000);
}
